/**
 * insDataType.js — the dataType visual language for the insurance catalog.
 *
 * Each insurance node carries a `DataType` (1-5) describing its product family.
 * This module is the single source of truth mapping that id to:
 *   - an i18n key (the Thai/EN label — keys already exist in src/languages/*.js)
 *   - a calm, colourblind-safe accent colour
 *
 * IMPORTANT: colour is never the only signal. The chip that consumes this map
 * always renders the translated label text alongside the colour dot, so the
 * five hues only need to be *distinguishable*, not individually decodable.
 *
 * Framework-free: plain ES module, no Vue / no i18n runtime in here.
 */

/**
 * @typedef {Object} DataTypeMeta
 * @property {number} id    DataType id (1-5, or 0 for the neutral fallback)
 * @property {string} key   i18n key resolved by the consumer via $t(key)
 * @property {string} color calm-derived accent hex
 */

/**
 * The five product families.
 *
 * Colours are hand-picked from a calm, low-saturation palette that stays
 * legible on the white (--c-surface) catalog background and is reasonably
 * separable for the common colour-vision deficiencies (deutan/protan/tritan):
 * blue / teal / amber / terracotta / violet — a spread across hue + lightness
 * rather than near-neighbours on the wheel.
 *
 *   1 Motor            (ประกันรถยนต์)          → calm blue       #3b6ea8  (the Calm primary)
 *   2 Health           (ประกันสุขภาพ)          → calm teal/green #38866b  (the Calm success)
 *   3 Casualty/Building (ประกันวินาศภัย อาคาร)  → calm amber      #c08527  (the Calm warning)
 *   4 Casualty/Transport(ประกันวินาศภัย ขนส่ง)  → terracotta      #b0563d
 *   5 Miscellaneous    (ประกันเบ็ดเตล็ด)        → muted violet    #6a5aa6
 */
export const INS_DATA_TYPES = [
  { id: 1, key: "lmg.insure_data_type_1", color: "#3b6ea8" },
  { id: 2, key: "lmg.insure_data_type_2", color: "#38866b" },
  { id: 3, key: "lmg.insure_data_type_3", color: "#c08527" },
  { id: 4, key: "lmg.insure_data_type_4", color: "#b0563d" },
  { id: 5, key: "lmg.insure_data_type_5", color: "#6a5aa6" },
];

/** Neutral fallback for unknown / missing DataType. */
const OTHER_META = {
  id: 0,
  key: "lmg.insure_data_type_other",
  color: "#8a96a8", // --c-text-mute
};

// id → meta lookup, built once.
const BY_ID = INS_DATA_TYPES.reduce((acc, meta) => {
  acc[meta.id] = meta;
  return acc;
}, {});

/**
 * Resolve the visual meta for a DataType id.
 * Tolerant of strings ("3"), null, undefined and out-of-range ids — anything
 * unrecognised maps to the neutral "other" meta so callers never crash.
 *
 * @param {number|string} id
 * @returns {DataTypeMeta}
 */
export function dataTypeMeta(id) {
  const n = Number(id);
  if (!Number.isNaN(n) && Object.prototype.hasOwnProperty.call(BY_ID, n)) {
    return BY_ID[n];
  }
  return OTHER_META;
}
